/**
 * VenueMap ✦ iter229 — interactive Leaflet map of every EventDrop event.
 *
 * One pin per completed public event (not per venue), package-coloured,
 * clustered when zoomed out, with a hover/click popup showing the
 * venue + event summary and a "View event →" link. Mobile renders a
 * bottom-sheet card instead of a tiny popup when a pin is tapped.
 *
 * Props:
 *   variant     "full" | "preview" (preview = smaller map, no filters)
 *   onPinClick  optional override; defaults to navigate(/events/{slug})
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#9C7C2E';
const CREAM = '#FFFCF6';
const PRIMARY = '#4B371E';
const TEXT_DIM = '#6B5E4F';

// Default centre — North Wales (Wrexham) — since 90% of Craig's
// venues are within ~50mi. Map auto-fits to bounds once pins load.
const UK_CENTRE = [53.0, -2.5];

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

// iter233 — premium pin. When the event has a cover_image we render a
// circular hero thumbnail with a package-coloured ring; otherwise we
// keep the iter229 coloured dot fallback. Featured pins get a larger
// gold ring + a small ★ overlay so they continue to stand out at a
// glance even when their hero photo is part of the pin.
const makePinIcon = (colour, isFeatured = false, coverImage = '') => {
  const size = isFeatured ? 44 : 36;
  const ringColor = isFeatured ? '#9C7C2E' : colour;
  const featuredStar = isFeatured ? `
    <div style="
      position: absolute; top: -4px; right: -4px;
      width: 18px; height: 18px; border-radius: 50%;
      background: #9C7C2E; color: #FFF8E7;
      font-family: serif; font-size: 11px;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    ">★</div>` : '';

  if (coverImage) {
    const imgUrl = coverImage.startsWith('/api/')
      ? `${process.env.REACT_APP_BACKEND_URL}${coverImage}`
      : coverImage;
    return L.divIcon({
      className: 'event-pin event-pin-hero',
      html: `
        <div style="position: relative; width: ${size}px; height: ${size}px;">
          <div style="
            width: ${size}px; height: ${size}px;
            border-radius: 50%;
            background-image: url('${imgUrl}');
            background-size: cover; background-position: center;
            border: 3px solid ${ringColor};
            box-shadow: 0 8px 22px rgba(0,0,0,0.38), 0 0 0 2px #FFF8E7;
            transition: transform 0.18s ease;
          "></div>
          ${featuredStar}
        </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  // Fallback — original coloured dot for events without a hero image
  const dot = isFeatured ? 30 : 24;
  return L.divIcon({
    className: 'event-pin event-pin-dot',
    html: `<div style="
      width: ${dot}px; height: ${dot}px; border-radius: 50%;
      background: ${colour};
      border: 2.5px solid #FFF8E7;
      box-shadow: 0 6px 16px rgba(0,0,0,0.32), 0 0 0 2px rgba(75,55,30,0.12);
      display: flex; align-items: center; justify-content: center;
      color: #FFF8E7; font-family: serif; font-size: 13px;
    ">${isFeatured ? '★' : ''}</div>`,
    iconSize: [dot, dot],
    iconAnchor: [dot / 2, dot / 2],
  });
};

const FitBoundsOnLoad = ({ pins }) => {
  const map = useMap();
  useEffect(() => {
    if (pins.length === 0) return;
    const bounds = L.latLngBounds(pins.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [pins, map]);
  return null;
};

const VenueMap = ({ variant = 'full' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // iter230 — pre-apply ?package= URL param so the "Seen it at"
  // badges can deep-link directly to the relevant pin set.
  const initialPackage = (variant === 'full' && searchParams.get('package')) || '';
  const [pins, setPins] = useState([]);
  const [colours, setColours] = useState({});
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState(initialPackage);
  const [yearFilter, setYearFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedPin, setSelectedPin] = useState(null); // mobile bottom-sheet
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 760);
  // iter229 — defer the MapContainer mount past React 18 StrictMode's
  // synchronous double-mount so leaflet doesn't throw "Map container
  // is already initialized." setTimeout 0 pushes the mount onto the
  // next macrotask, after both StrictMode effect passes.
  const [mapMounted, setMapMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMapMounted(true), 0);
    return () => { clearTimeout(t); setMapMounted(false); };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (eventTypeFilter) params.event_type = eventTypeFilter;
        if (packageFilter) params.package = packageFilter;
        if (yearFilter) params.year = yearFilter;
        if (search) params.search = search;
        const r = await axios.get(`${API}/venues/map`, { params });
        if (cancelled) return;
        setPins(r.data?.pins || []);
        setColours(r.data?.package_colours || {});
      } catch (e) { /* silent — empty map shows below */ }
    })();
    return () => { cancelled = true; };
  }, [eventTypeFilter, packageFilter, yearFilter, search]);

  // Derived filter options from the live pin set so we never show
  // an event-type chip that doesn't apply. iter230 — include the
  // initialPackage (from ?package=) in the package options even when
  // no pins currently match, so the <select> visually reflects the
  // applied filter when a customer deep-links from a SeenItAtBadge.
  const eventTypes = useMemo(() => {
    const s = new Set(pins.map(p => p.event_type).filter(Boolean));
    return Array.from(s).sort();
  }, [pins]);
  const packages = useMemo(() => {
    const s = new Set(pins.map(p => p.package_slug).filter(Boolean));
    if (packageFilter) s.add(packageFilter);
    return Array.from(s).sort();
  }, [pins, packageFilter]);
  const years = useMemo(() => {
    const s = new Set(pins.map(p => (p.event_date || '').slice(0, 4)).filter(Boolean));
    return Array.from(s).sort().reverse();
  }, [pins]);

  const mapHeight = variant === 'preview' ? 360 : 'clamp(520px, 70vh, 720px)';

  const handlePinClick = (pin) => {
    if (isMobile) setSelectedPin(pin);
    else navigate(`/events/${pin.slug}`);
  };

  return (
    <div data-testid={`venue-map-${variant}`} style={{ position: 'relative' }}>
      {variant === 'full' && (
        <Filters
          eventTypes={eventTypes} packages={packages} years={years}
          eventTypeFilter={eventTypeFilter} setEventTypeFilter={setEventTypeFilter}
          packageFilter={packageFilter} setPackageFilter={setPackageFilter}
          yearFilter={yearFilter} setYearFilter={setYearFilter}
          search={search} setSearch={setSearch}
          pinCount={pins.length}
        />
      )}
      <div style={{
        height: mapHeight, borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 22px 60px rgba(75,55,30,0.16)',
        border: `1px solid rgba(122,92,62,0.16)`,
        background: '#E8E2D5',
      }}>
        {mapMounted && (
        <MapContainer
          center={UK_CENTRE} zoom={7} scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBoundsOnLoad pins={pins} />
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={(cluster) => L.divIcon({
              html: `<div style="
                width: 42px; height: 42px; border-radius: 50%;
                background: ${GOLD}; color: #FFF8E7; font-family: serif;
                font-weight: 500; font-size: 15px;
                display: flex; align-items: center; justify-content: center;
                box-shadow: 0 8px 24px rgba(0,0,0,0.35), 0 0 0 6px rgba(156,124,46,0.22);
              ">${cluster.getChildCount()}</div>`,
              className: 'event-cluster',
              iconSize: [42, 42],
            })}
          >
            {pins.map(pin => (
              <Marker
                key={pin.event_id}
                position={[pin.lat, pin.lng]}
                icon={makePinIcon(pin.pin_colour, pin.is_featured, pin.cover_image)}
                eventHandlers={{
                  click: () => handlePinClick(pin),
                  mouseover: (e) => { if (!isMobile) e.target.openPopup(); },
                }}
              >
                {!isMobile && <PinPopup pin={pin} />}
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
        )}
      </div>
      {variant === 'full' && <Legend colours={colours} />}
      {isMobile && selectedPin && (
        <MobileBottomSheet pin={selectedPin} onClose={() => setSelectedPin(null)} />
      )}
    </div>
  );
};

// iter230 — Premium popup. When the venue has hosted multiple events
// it shows the "This venue has hosted X events" intelligence panel
// with the last three events; otherwise renders the standard
// single-event card. Fetches venue intelligence lazily on first open.
const PinPopup = ({ pin }) => {
  const [intel, setIntel] = useState(null);
  const [loadedFor, setLoadedFor] = useState(null);
  const eventsHere = intel?.stats?.events_completed ?? null;

  // Trigger intelligence load when popup actually opens
  const onOpen = async () => {
    if (!pin.venue_slug || loadedFor === pin.venue_slug) return;
    setLoadedFor(pin.venue_slug);
    try {
      const r = await axios.get(`${API}/venues/${pin.venue_slug}/intelligence`);
      setIntel(r.data || null);
    } catch { /* fall back to single-event card */ }
  };

  return (
    <Popup
      eventHandlers={{ add: onOpen }}
      data-testid={`pin-popup-${pin.event_id}`}
      maxWidth={320} minWidth={280}
    >
      <div style={{ fontFamily: SERIF, color: PRIMARY, padding: 4 }}>
        {pin.cover_image && (
          <div style={{
            backgroundImage: `url(${mediaUrl(pin.cover_image)})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            aspectRatio: '16/9', borderRadius: 6, marginBottom: 8,
          }} />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between',
                       alignItems: 'baseline', gap: 8 }}>
          <div style={{ fontWeight: 500, fontSize: 15, color: PRIMARY,
                         letterSpacing: '-0.005em' }}>
            {pin.venue_name || pin.title}
          </div>
          {pin.is_featured && (
            <span style={{
              background: GOLD, color: '#FFF8E7', fontFamily: SERIF,
              fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
              padding: '2px 7px', borderRadius: 999,
            }}>★ Featured</span>
          )}
        </div>
        <div style={{ fontStyle: 'italic', fontSize: 11.5, color: TEXT_DIM, marginTop: 3 }}>
          {[pin.county, pin.event_type, pin.package_label || pin.package_slug]
            .filter(Boolean).join(' · ')}
          {pin.event_date ? ` · ${pin.event_date.slice(0, 10)}` : ''}
        </div>
        {pin.rating > 0 && (
          <div style={{ color: GOLD, fontSize: 13, letterSpacing: 1.5, marginTop: 6 }}>
            {'★'.repeat(Math.round(pin.rating))}
          </div>
        )}
        {pin.review_snippet && (
          <div style={{
            fontStyle: 'italic', fontSize: 12.5, color: PRIMARY,
            margin: '6px 0 10px', lineHeight: 1.5,
          }}>&ldquo;{pin.review_snippet.slice(0, 110)}{pin.review_snippet.length > 110 ? '…' : ''}&rdquo;</div>
        )}

        {/* iter230 — multi-event panel */}
        {eventsHere !== null && eventsHere > 1 && intel && (
          <div data-testid={`pin-popup-multi-${pin.venue_slug}`} style={{
            marginTop: 10, paddingTop: 10,
            borderTop: '1px solid rgba(75,55,30,0.12)',
          }}>
            <div style={{
              fontStyle: 'italic', fontSize: 10.5,
              letterSpacing: '0.22em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 6,
            }}>
              {(intel.confidence?.label || '✦ EventDrop venue')}
            </div>
            <div style={{ fontSize: 12.5, color: PRIMARY, marginBottom: 8 }}>
              <strong>This venue has hosted {eventsHere} EventDrop event{eventsHere === 1 ? '' : 's'}</strong>
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
              {intel.events.slice(0, 3).map(e => (
                <li key={e.event_id} style={{
                  fontSize: 11.5, padding: '3px 0', color: PRIMARY,
                  display: 'flex', justifyContent: 'space-between', gap: 8,
                }}>
                  <span>
                    {[e.event_type, e.package_label || e.package_slug].filter(Boolean).join(' · ')}
                  </span>
                  <span style={{ color: TEXT_DIM, fontStyle: 'italic', flexShrink: 0 }}>
                    {(e.event_date || '').slice(0, 10)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
          <Link to={`/events/${pin.slug}`} data-testid={`pin-link-${pin.event_id}`} style={{
            color: GOLD, fontSize: 12.5, fontStyle: 'italic',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}>View this event →</Link>
          {pin.venue_slug && (
            <Link to={`/venues/${pin.venue_slug}`} data-testid={`pin-venue-link-${pin.venue_slug}`} style={{
              color: GOLD, fontSize: 12.5, fontStyle: 'italic',
              textDecoration: 'underline', textUnderlineOffset: 3,
              marginLeft: 'auto',
            }}>View all events at venue →</Link>
          )}
        </div>
      </div>
    </Popup>
  );
};

const MobileBottomSheet = ({ pin, onClose }) => {
  const navigate = useNavigate();
  return (
    <div data-testid="venue-map-bottom-sheet" onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,11,9,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-end',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: CREAM, width: '100%', borderTopLeftRadius: 18, borderTopRightRadius: 18,
        padding: '20px 22px 28px', boxShadow: '0 -16px 40px rgba(0,0,0,0.3)',
        fontFamily: SERIF, color: PRIMARY,
      }}>
        <div style={{ width: 48, height: 4, background: 'rgba(75,55,30,0.18)',
          borderRadius: 999, margin: '0 auto 16px' }} />
        {pin.cover_image && (
          <div style={{
            backgroundImage: `url(${mediaUrl(pin.cover_image)})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            aspectRatio: '16/9', borderRadius: 10, marginBottom: 12,
          }} />
        )}
        <div style={{ fontWeight: 500, fontSize: 18, color: PRIMARY, marginBottom: 4 }}>
          {pin.venue_name || pin.title}
        </div>
        <div style={{ fontStyle: 'italic', fontSize: 13, color: TEXT_DIM, marginBottom: 10 }}>
          {[pin.event_type, pin.package_label || pin.package_slug, pin.event_date?.slice(0, 10)]
            .filter(Boolean).join(' · ')}
        </div>
        {pin.rating > 0 && (
          <div style={{ color: GOLD, fontSize: 16, letterSpacing: 2 }}>
            {'★'.repeat(Math.round(pin.rating))}
          </div>
        )}
        {pin.review_snippet && (
          <p style={{ fontStyle: 'italic', fontSize: 13.5, lineHeight: 1.6, margin: '10px 0 14px' }}>
            &ldquo;{pin.review_snippet}&rdquo;
          </p>
        )}
        <button type="button" onClick={() => navigate(`/events/${pin.slug}`)}
          data-testid="venue-map-bottom-sheet-cta" style={{
          background: GOLD, color: '#FFF8E7', border: 'none', borderRadius: 999,
          padding: '12px 22px', fontFamily: SERIF, fontSize: 13,
          letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer', width: '100%',
        }}>View this event ✦</button>
      </div>
    </div>
  );
};

const Filters = ({ eventTypes, packages, years, eventTypeFilter, setEventTypeFilter,
                   packageFilter, setPackageFilter, yearFilter, setYearFilter,
                   search, setSearch, pinCount }) => (
  <div data-testid="venue-map-filters" style={{
    display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
    marginBottom: 16, padding: '14px 16px', background: '#FFFFFF',
    border: '1px solid rgba(122,92,62,0.14)', borderRadius: 12,
    boxShadow: '0 12px 32px rgba(75,55,30,0.08)',
  }}>
    <input
      placeholder="Search venue, town, county…"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      data-testid="venue-map-search"
      style={{
        flex: '1 1 240px', minWidth: 200, padding: '9px 13px',
        borderRadius: 8, border: '1px solid rgba(122,92,62,0.20)',
        fontFamily: SERIF, fontSize: 14, color: PRIMARY,
      }}
    />
    <FilterSelect label="Event" value={eventTypeFilter} onChange={setEventTypeFilter} options={eventTypes} testid="venue-map-filter-event" />
    <FilterSelect label="Package" value={packageFilter} onChange={setPackageFilter} options={packages} testid="venue-map-filter-package" />
    <FilterSelect label="Year" value={yearFilter} onChange={setYearFilter} options={years} testid="venue-map-filter-year" />
    <div style={{
      marginLeft: 'auto', fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12.5, color: TEXT_DIM,
    }} data-testid="venue-map-pin-count">
      {pinCount} event{pinCount === 1 ? '' : 's'} pinned
    </div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    style={{
      padding: '9px 12px', borderRadius: 8, fontFamily: SERIF, fontSize: 13.5,
      border: '1px solid rgba(122,92,62,0.20)', color: PRIMARY, background: '#FFFFFF',
      cursor: 'pointer',
    }}>
    <option value="">All {label.toLowerCase()}s</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const Legend = ({ colours }) => {
  if (!colours || Object.keys(colours).length === 0) return null;
  return (
    <div data-testid="venue-map-legend" style={{
      display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 14,
      fontFamily: SERIF, fontSize: 12, color: TEXT_DIM,
      padding: '12px 14px', background: 'rgba(122,92,62,0.05)', borderRadius: 10,
    }}>
      <span style={{ fontStyle: 'italic', letterSpacing: '0.16em',
        textTransform: 'uppercase', color: GOLD, marginRight: 8 }}>✦ Pin colours</span>
      {Object.entries(colours).filter(([k]) => k !== 'default').map(([k, c]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            background: c, border: '2px solid #FFF8E7',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          }} />
          {k.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </span>
      ))}
    </div>
  );
};

export default VenueMap;
